import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  formatDate,
  formatDateShort,
  formatDateTime,
  formatTimeNow,
  setActiveTimezone,
} from 'utils/date.util';
import { getAppSettingsApi, updateAppTimezoneApi } from 'features/app-settings/app-settings.api';
import { buildTimezoneOptions } from 'constants/timezones';

const TimezoneContext = createContext(null);

export const TimezoneProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { timezone, displayTime, saving, loaded } = useSelector((state) => state.appSettings);
  const [clock, setClock] = useState('');

  useEffect(() => {
    dispatch(getAppSettingsApi());
  }, [dispatch]);

  const prevTimezoneRef = useRef(null);
  if (timezone && timezone !== prevTimezoneRef.current) {
    setActiveTimezone(timezone);
    prevTimezoneRef.current = timezone;
  }

  useEffect(() => {
    const tick = () => setClock(formatTimeNow(timezone));
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [timezone]);

  const setTimezone = useCallback(
    async (nextTimezone) => {
      const result = await dispatch(updateAppTimezoneApi(nextTimezone));
      if (updateAppTimezoneApi.fulfilled.match(result)) {
        setActiveTimezone(nextTimezone);
      }
      return result;
    },
    [dispatch],
  );

  const timezoneOptions = useMemo(() => buildTimezoneOptions(timezone), [timezone]);

  const value = useMemo(
    () => ({
      timezone,
      displayTime: clock || displayTime,
      timezoneOptions,
      loaded,
      saving,
      setTimezone,
      formatDateTime: (iso, options) => formatDateTime(iso, { ...options, timezone }),
      formatDate: (iso, options) => formatDate(iso, { ...options, timezone }),
      formatDateShort: (iso, options) => formatDateShort(iso, { ...options, timezone }),
    }),
    [timezone, clock, displayTime, timezoneOptions, loaded, saving, setTimezone],
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
};

export const useFormatDate = () => {
  const { formatDateTime: fmtDateTime, formatDate: fmtDate, formatDateShort: fmtShort, timezone } =
    useTimezone();
  return {
    timezone,
    formatDateTime: fmtDateTime,
    formatDate: fmtDate,
    formatDateShort: fmtShort,
  };
};
